import React, { useCallback, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import { CountdownTimerProps, Time } from '../types';
const TimeUnit = dynamic(() => import('./time-unit').then(module => module.default), {
  ssr: false
});
const CountdownTimer: React.FC<CountdownTimerProps> = ({
  endTime,
  textColor,
  className
}) => {
  const calculateTimeLeft = useCallback((): Time<number> => {
    const difference = new Date(endTime).getTime() - new Date().getTime();
    let timeLeft: Time<number> = {
      months: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
    if (difference > 0) {
      timeLeft = {
        months: Math.floor(difference / (1000 * 60 * 60 * 24 * 30)),
        days: Math.floor(difference / (1000 * 60 * 60 * 24) % 30),
        hours: Math.floor(difference / (1000 * 60 * 60) % 24),
        minutes: Math.floor(difference / 1000 / 60 % 60),
        seconds: Math.floor(difference / 1000 % 60)
      };
    }
    return timeLeft;
  }, [endTime]);
  const [timeLeft, setTimeLeft] = useState<Time<number>>(calculateTimeLeft());
  const [prevTimeLeft, setPrevTimeLeft] = useState<Time<number>>(timeLeft);
  const [isFlipping, setIsFlipping] = useState<Time<boolean>>({
    months: true,
    days: true,
    hours: true,
    minutes: true,
    seconds: true
  });
  useEffect(() => {
    const timer = setInterval(() => {
      if (!timeLeft.seconds && !timeLeft.minutes && !timeLeft.hours && !timeLeft.days && !timeLeft.months) {
        setIsFlipping({
          months: false,
          days: false,
          hours: false,
          minutes: false,
          seconds: false
        });
      } else {
        setIsFlipping({
          months: !timeLeft.days && !timeLeft.hours && !timeLeft.minutes && !timeLeft.seconds ? !isFlipping.months : isFlipping.months,
          days: !timeLeft.hours && !timeLeft.minutes && !timeLeft.seconds ? !isFlipping.days : isFlipping.days,
          hours: !timeLeft.minutes && !timeLeft.seconds ? !isFlipping.hours : isFlipping.hours,
          minutes: !timeLeft.seconds ? !isFlipping.minutes : isFlipping.minutes,
          seconds: timeLeft.seconds !== prevTimeLeft.seconds ? !isFlipping.seconds : isFlipping.seconds
        });
      }
      setPrevTimeLeft(timeLeft);
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [calculateTimeLeft, isFlipping, prevTimeLeft, timeLeft]);
  const hasTime = !!(timeLeft.seconds || timeLeft.minutes || timeLeft.hours || timeLeft.days || timeLeft.months);
  return <div className={classNames('left-to-right flex gap-4 md:gap-6', className)} style={{
    color: textColor
  }} data-sentry-component="CountdownTimer" data-sentry-source-file="countdown-timer.tsx">
            {Object.keys(timeLeft).map(interval => {
      const unit = interval as keyof Time<number>;
      if (unit === 'months' && !timeLeft[unit]) {
        return null;
      }
      return <TimeUnit key={unit} current={timeLeft[unit]} previous={prevTimeLeft[unit]} isFlipping={isFlipping[unit]} unit={unit} hasTime={hasTime} />;
    })}
        </div>;
};
export default CountdownTimer;